import { supabase } from "~/integrations/supabase/client";
import type {
  BalanceSheetFilters,
  BalanceSheetLine,
  BalanceSheetGroupedData,
  BalanceSheetClase,
  BalanceSheetSubClase,
  BalanceSheetSubClase2,
  BalanceSheetGrupo,
} from "~/types/contabilidad";

export const balanceSheetService = {
  /**
   * Fetch balance sheet data from the database
   */
  async getBalanceSheet(
    filters: BalanceSheetFilters
  ): Promise<BalanceSheetLine[]> {
    const { data, error } = await supabase.rpc("get_balance_sheet", {
      p_date_from: filters.dateFrom,
      p_date_to: filters.dateTo,
      p_company_id: filters.companyId,
      p_template_id: filters.templateId,
    });

    if (error) throw error;
    return data as BalanceSheetLine[];
  },

  /**
   * Transform flat balance sheet lines into hierarchical grouped data
   */
  groupBalanceSheetData(lines: BalanceSheetLine[]): BalanceSheetGroupedData {
    const clasesMap = new Map<string, BalanceSheetClase>();

    for (const line of lines) {
      // Get or create clase
      let clase = clasesMap.get(line.clase);
      if (!clase) {
        clase = {
          clase: line.clase,
          orden_clase: line.orden_clase,
          total: 0,
          subClases: [],
        };
        clasesMap.set(line.clase, clase);
      }

      // Find or create sub_clase
      let subClase = clase.subClases.find(
        (sc) => sc.sub_clase === line.sub_clase
      );
      if (!subClase) {
        subClase = {
          sub_clase: line.sub_clase,
          orden_subclase: line.orden_subclase,
          total: 0,
          subClases2: [],
        };
        clase.subClases.push(subClase);
      }

      // Find or create sub_clase_2
      let subClase2 = subClase.subClases2.find(
        (sc2) => sc2.sub_clase_2 === line.sub_clase_2
      );
      if (!subClase2) {
        subClase2 = {
          sub_clase_2: line.sub_clase_2,
          orden_subclase_2: line.orden_subclase_2,
          total: 0,
          grupos: [],
        };
        subClase.subClases2.push(subClase2);
      }

      // Find or create grupo
      // Use a composite key since grupo_cuenta_id can be null
      const grupoKey = line.grupo_cuenta_id || `ungrouped-${line.sub_clase_2}`;
      let grupo = subClase2.grupos.find(
        (g) =>
          (g.grupo_cuenta_id || `ungrouped-${line.sub_clase_2}`) === grupoKey
      );
      if (!grupo) {
        grupo = {
          grupo_cuenta_id: line.grupo_cuenta_id,
          grupo_cuenta_code: line.grupo_cuenta_code,
          grupo_cuenta_name: line.grupo_cuenta_name,
          orden_grupo_cuenta: line.orden_grupo_cuenta,
          total: 0,
          accounts: [],
        };
        subClase2.grupos.push(grupo);
      }

      // Add account to grupo
      grupo.accounts.push({
        account_id: line.account_id,
        account_code: line.account_code,
        account_name: line.account_name,
        saldo_final: line.saldo_final,
      });

      // Accumulate totals
      grupo.total += line.saldo_final;
      subClase2.total += line.saldo_final;
      subClase.total += line.saldo_final;
      clase.total += line.saldo_final;
    }

    // Convert map to sorted array
    const clases = Array.from(clasesMap.values()).sort(
      (a, b) => a.orden_clase - b.orden_clase
    );

    // Sort nested arrays
    for (const clase of clases) {
      clase.subClases.sort((a, b) => a.orden_subclase - b.orden_subclase);
      for (const subClase of clase.subClases) {
        subClase.subClases2.sort(
          (a, b) => a.orden_subclase_2 - b.orden_subclase_2
        );
        for (const subClase2 of subClase.subClases2) {
          subClase2.grupos.sort(
            (a, b) => a.orden_grupo_cuenta - b.orden_grupo_cuenta
          );
          // Accounts are already sorted by code from the database
        }
      }
    }

    // Calculate totals
    const totalActivo =
      clases.find((c) => c.clase === "Activo")?.total || 0;

    // For Pasivo y Patrimonio, we need to sum up Pasivo and Patrimonio sub_clases
    const pasivoYPatrimonioClase = clases.find(
      (c) => c.clase === "Pasivo y Patrimonio"
    );
    const totalPasivoYPatrimonio = pasivoYPatrimonioClase?.total || 0;

    const diferencia = totalActivo - totalPasivoYPatrimonio;

    return {
      clases,
      totalActivo,
      totalPasivoYPatrimonio,
      diferencia,
    };
  },
};
